import { fetchApi } from './api';
import { clearCurrentUser } from './auth';

const SESSION_KEY = 'app_session_id';
let validationInterval = null;

const generateSessionId = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};

const getSessionId = () => sessionStorage.getItem(SESSION_KEY);

/**
 * Register a new session on the server. The server stores only the latest
 * session ID per user, effectively invalidating any previous session.
 */
const registerSession = async (userEmail) => {
  const sessionId = generateSessionId();
  sessionStorage.setItem(SESSION_KEY, sessionId);
  try {
    await fetchApi('/api/session/register', {
      method: 'POST',
      body: JSON.stringify({ email: userEmail, sessionId }),
      skipLoader: true
    });
  } catch (error) {
    console.error('[SessionService] Failed to register session:', error);
  }
  return sessionId;
};

/**
 * Validate current session against server. Returns false if another
 * device/browser has logged in with the same account.
 */
const validateSession = async (userEmail) => {
  const sessionId = getSessionId();
  if (!sessionId || !userEmail) return false;
  try {
    const response = await fetchApi('/api/session/validate', {
      method: 'POST',
      body: JSON.stringify({ email: userEmail, sessionId }),
      skipLoader: true
    });
    return response.valid === true;
  } catch (error) {
    console.error('[SessionService] Session validation failed:', error);
    return true; // Allow on network error to avoid false lockouts
  }
};

/**
 * Start periodic validation. If session becomes invalid, force logout.
 */
const startSessionValidation = (userEmail, onInvalid) => {
  stopSessionValidation();
  validationInterval = setInterval(async () => {
    const isValid = await validateSession(userEmail);
    if (!isValid) {
      stopSessionValidation();
      onInvalid();
    }
  }, 10000); // Check every 10 seconds
};

const stopSessionValidation = () => {
  if (validationInterval) {
    clearInterval(validationInterval);
    validationInterval = null;
  }
};

/**
 * Force logout when concurrent login is detected.
 */
const handleConcurrentLogin = (instance, navigate) => {
  stopSessionValidation();
  sessionStorage.removeItem(SESSION_KEY);
  clearCurrentUser();
  sessionStorage.clear();
  if (instance) {
    try {
      instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin + '/login' });
    } catch {
      navigate('/login', { replace: true });
      window.location.reload();
    }
  } else {
    navigate('/login', { replace: true });
    window.location.reload();
  }
};

const SessionService = {
  registerSession,
  validateSession,
  startSessionValidation,
  stopSessionValidation,
  handleConcurrentLogin,
  getSessionId
};

export default SessionService;
